import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  Users, AlertCircle, CheckCircle2, Clock, AlertTriangle,
  Search, Filter, ChevronLeft, ChevronRight, ExternalLink,
  Plus, RefreshCw, LayoutDashboard, FileText, Activity, LogOut, Download, CalendarIcon, Copy
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const regionBadges = {
  usa: { label: 'USA', emoji: '🇺🇸', color: 'bg-blue-100 text-blue-800' },
  canada: { label: 'Canada', emoji: '🇨🇦', color: 'bg-red-100 text-red-800' },
  jamaica: { label: 'Jamaica', emoji: '🇯🇲', color: 'bg-green-100 text-green-800' },
  'southern caribbean': { label: 'S. Caribbean', emoji: '🇧🇧', color: 'bg-yellow-100 text-yellow-800' }
};

const statusColors = {
  pending: 'bg-gray-100 text-gray-800',
  invited: 'bg-blue-100 text-blue-800',
  started: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  submitted: 'bg-green-100 text-green-800',
  validated: 'bg-emerald-100 text-emerald-800'
};

// Auth check hook for support pages
const useAuthCheck = (navigate) => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const userInfo = localStorage.getItem('userInfo');
      
      if (!token || !userInfo) {
        toast.error('Please login to access the Support Dashboard');
        navigate('/login');
        return;
      }

      try {
        const user = JSON.parse(userInfo);
        if (user.role !== 'internal_team' && user.role !== 'admin') {
          toast.error('Access denied. Internal team role required.');
          navigate('/login');
          return;
        }
        setIsAuthorized(true);
      } catch (e) {
        toast.error('Session expired. Please login again.');
        navigate('/login');
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]);

  return { isAuthorized, isChecking };
};

export default function SupportDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthorized, isChecking } = useAuthCheck(navigate);
  
  const [metrics, setMetrics] = useState({
    totalAssessments: 0,
    activeIssues: 0,
    autoFixedToday: 0,
    awaitingDeployment: 0,
    escalated: 0
  });
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  const token = localStorage.getItem('token');

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/support/dashboard/metrics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    }
  }, [token]);

  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '25'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (regionFilter !== 'all') params.append('region', regionFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('assessment_type', typeFilter);

      const response = await fetch(`${API_URL}/api/support/assessments?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAssessments(data.assessments);
        setTotalPages(data.totalPages);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  }, [token, page, searchQuery, regionFilter, statusFilter, typeFilter]);

  useEffect(() => {
    if (isAuthorized) {
      fetchMetrics();
      fetchAssessments();
    }
  }, [fetchMetrics, fetchAssessments, isAuthorized]);

  // Show loading while checking auth
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Don't render if not authorized (will redirect)
  if (!isAuthorized) {
    return null;
  }

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchAssessments();
  };

  const handleReportIssue = (assessment) => {
    navigate('/support/issues/new', { 
      state: { 
        candidateId: assessment.id,
        employeeName: assessment.fullName,
        employeeEmail: assessment.email,
        employeeRole: assessment.jobTitle,
        employeeRegion: assessment.region,
        assessmentLink: assessment.assessmentLink
      } 
    });
  };

  const exportAssessments = async () => {
    try {
      const body = {
        region: regionFilter,
        status: statusFilter,
        search: searchQuery,
        assessmentType: typeFilter,
      };
      if (dateRange.from) body.dateFrom = format(dateRange.from, 'yyyy-MM-dd');
      if (dateRange.to) body.dateTo = format(dateRange.to, 'yyyy-MM-dd');
      
      const res = await fetch(`${API_URL}/api/super-support/export-assessments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `assessments_export_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Export downloaded');
      } else {
        toast.error('Export failed');
      }
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const getRegionBadge = (region) => {
    const regionKey = region?.toLowerCase();
    const badge = regionBadges[regionKey] || { label: region || 'Unknown', emoji: '🌐', color: 'bg-gray-100 text-gray-800' };
    return (
      <Badge className={`${badge.color} font-normal`}>
        {badge.emoji} {badge.label}
      </Badge>
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="support-dashboard">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r shadow-sm hidden md:block">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Support Portal</h2>
          <p className="text-sm text-gray-500">THCO Internal Team</p>
        </div>
        <nav className="p-4 space-y-2">
          <Link 
            to="/support" 
            className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
              location.pathname === '/support' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/support/issues" 
            className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith('/support/issues') ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <FileText className="w-5 h-5" />
            Issues
            {metrics.activeIssues > 0 && (
              <span className="ml-auto bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">
                {metrics.activeIssues}
              </span>
            )}
          </Link>
          <Link 
            to="/support/health" 
            className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
              location.pathname === '/support/health' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Activity className="w-5 h-5" />
            System Health
          </Link>
        </nav>
        <div className="absolute bottom-0 left-0 w-64 p-4 border-t bg-white">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-gray-600 hover:text-red-600"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Support Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor assessments and manage support issues</p>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card data-testid="metric-total">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Assessments</p>
                  <p className="text-3xl font-bold text-gray-900">{metrics.totalAssessments}</p>
                </div>
                <Users className="w-10 h-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="metric-active">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Issues</p>
                  <p className="text-3xl font-bold text-orange-600">{metrics.activeIssues}</p>
                </div>
                <AlertCircle className="w-10 h-10 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="metric-fixed">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Auto-Fixed Today</p>
                  <p className="text-3xl font-bold text-green-600">{metrics.autoFixedToday}</p>
                </div>
                <CheckCircle2 className="w-10 h-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="metric-awaiting">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Awaiting Approval</p>
                  <p className="text-3xl font-bold text-yellow-600">{metrics.awaitingDeployment}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="metric-escalated">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Escalated</p>
                  <p className="text-3xl font-bold text-red-600">{metrics.escalated}</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-red-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Assessment Table */}
        <Card>
          <CardHeader className="border-b">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle>Assessment Progress</CardTitle>
              
              <div className="flex flex-col md:flex-row gap-3">
                {/* Search */}
                <form onSubmit={handleSearch} className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-64"
                      data-testid="search-input"
                    />
                  </div>
                  <Button type="submit" variant="outline" size="icon">
                    <Search className="w-4 h-4" />
                  </Button>
                </form>

                {/* Filters */}
                <Select value={regionFilter} onValueChange={(v) => { setRegionFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-40" data-testid="region-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Region" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Regions</SelectItem>
                    <SelectItem value="usa">USA</SelectItem>
                    <SelectItem value="canada">Canada</SelectItem>
                    <SelectItem value="jamaica">Jamaica</SelectItem>
                    <SelectItem value="barbados">Barbados</SelectItem>
                    <SelectItem value="trinidad_tobago">Trinidad & Tobago</SelectItem>
                    <SelectItem value="panama">Panama</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-40" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="started">Started</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-40" data-testid="type-filter">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="self_assessment">Self Assessment</SelectItem>
                    <SelectItem value="manager_validation">Manager Validation</SelectItem>
                    <SelectItem value="technical_assessment">Technical Assessment</SelectItem>
                    <SelectItem value="hands_on_simulation">Hands-On Simulation</SelectItem>
                  </SelectContent>
                </Select>

                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="text-sm" data-testid="support-date-filter-btn">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {dateRange.from ? (
                        dateRange.to ? (
                          <>{format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d')}</>
                        ) : format(dateRange.from, 'MMM d, yyyy')
                      ) : 'Date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => {
                        setDateRange(range || { from: undefined, to: undefined });
                        if (range?.to) setDatePopoverOpen(false);
                      }}
                      numberOfMonths={2}
                    />
                    {dateRange.from && (
                      <div className="px-3 pb-3 flex justify-end">
                        <Button variant="ghost" size="sm" onClick={() => { setDateRange({ from: undefined, to: undefined }); setDatePopoverOpen(false); }}>
                          Clear
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>

                <Button 
                  variant="outline"
                  onClick={exportAssessments}
                  data-testid="support-export-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>

                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => { fetchMetrics(); fetchAssessments(); }}
                  data-testid="refresh-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="assessments-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium text-gray-600">Name</th>
                        <th className="text-left p-4 font-medium text-gray-600">Email</th>
                        <th className="text-left p-4 font-medium text-gray-600">Type</th>
                        <th className="text-left p-4 font-medium text-gray-600">Assessment Link</th>
                        <th className="text-left p-4 font-medium text-gray-600">Region</th>
                        <th className="text-left p-4 font-medium text-gray-600">Status</th>
                        <th className="text-left p-4 font-medium text-gray-600">Progress</th>
                        <th className="text-left p-4 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {assessments.map((assessment) => {
                        const fullLink = `${window.location.origin}${assessment.assessmentLink}`;
                        return (
                        <tr key={assessment.id} className="hover:bg-gray-50">
                          <td className="p-4 font-medium text-gray-900">{assessment.fullName}</td>
                          <td className="p-4 text-gray-600 text-sm">{assessment.email}</td>
                          <td className="p-4">
                            <Badge className={
                              assessment.assessmentType === 'technical_assessment' ? 'bg-sky-100 text-sky-800' :
                              assessment.assessmentType === 'hands_on_simulation' ? 'bg-purple-100 text-purple-800' :
                              assessment.assessmentType === 'manager_validation' ? 'bg-amber-100 text-amber-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {assessment.assessmentType === 'technical_assessment' ? 'TA' :
                               assessment.assessmentType === 'hands_on_simulation' ? 'HOS' :
                               assessment.assessmentType === 'manager_validation' ? 'MV' : 'SA'}
                            </Badge>
                          </td>
                          <td className="p-4">
                            {assessment.assessmentLink ? (
                              <code
                                className="text-[11px] bg-slate-50 border px-1.5 py-0.5 rounded max-w-[180px] truncate block cursor-pointer hover:bg-slate-100 transition-colors"
                                title={fullLink}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await navigator.clipboard.writeText(fullLink);
                                    toast.success("Link copied!");
                                  } catch { toast.error("Failed to copy"); }
                                }}
                                data-testid={`support-link-${assessment.id}`}
                              >
                                {fullLink}
                              </code>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="p-4">{getRegionBadge(assessment.region)}</td>
                          <td className="p-4">
                            <Badge className={statusColors[assessment.status] || 'bg-gray-100 text-gray-800'}>
                              {assessment.status}
                            </Badge>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full" 
                                  style={{ width: `${assessment.progressPercentage}%` }}
                                />
                              </div>
                              <span className="text-sm text-gray-600">{assessment.progressPercentage}%</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReportIssue(assessment)}
                                data-testid={`report-issue-${assessment.id}`}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Report Issue
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(fullLink, '_blank')}
                                data-testid={`view-assessment-${assessment.id}`}
                              >
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-gray-600">
                    Showing {((page - 1) * 25) + 1} to {Math.min(page * 25, total)} of {total} assessments
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      data-testid="next-page"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        </div>
      </main>
    </div>
  );
}
